import { useMutation } from '@apollo/client'
import {
  Button,
  CircularProgress,
  Grid,
  Paper,
  TextField,
  Typography
} from '@material-ui/core'
import Alert from '@material-ui/lab/Alert'
import React, { useRef, useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'

import PageHeader from '../../components/PageHeader'
import {
  UPLOAD_VOUCHERS_MUTATION,
  VoucherUploadResult
} from '../../graphql/promotions'
import { getTranslate, lang } from '../../localization'
import { LocalizedLink } from '../../routes/localizedRoutes'
import PromotionCodesSection from './codes/PromotionCodesSection'
import useStyles from './styles'

const VoucherUpload: React.FC = () => {
  const MAX_CODES = 100000
  // Keep JSON/GraphQL/protobuf overhead below the service receive limit.
  const MAX_FILE_BYTES = 6 * 1024 * 1024
  const classes = useStyles()
  const history = useHistory()
  const { id } = useParams<{ id: string }>()
  const [codes, setCodes] = useState('')
  const [result, setResult] = useState<VoucherUploadResult | null>(null)
  const [inputError, setInputError] = useState('')
  const [validatedCodes, setValidatedCodes] = useState<string | null>(null)
  const [committed, setCommitted] = useState(false)
  const codesRef = useRef(codes)
  const [upload, { loading }] = useMutation<{
    uploadVouchers: VoucherUploadResult
  }>(UPLOAD_VOUCHERS_MUTATION)

  const updateCodes = (nextCodes: string) => {
    codesRef.current = nextCodes
    setCodes(nextCodes)
    setInputError('')
    setValidatedCodes(null)
    setResult(null)
    setCommitted(false)
  }

  const submit = (commit: boolean) => {
    const normalizedCodes = codes
      .split(/\r?\n/)
      .map((code) => code.trim())
      .filter(Boolean)
    if (!normalizedCodes.length) {
      setResult(null)
      setInputError(getTranslate('Enter at least one voucher code.'))
      return
    }
    if (normalizedCodes.length > MAX_CODES) {
      setResult(null)
      setInputError(
        getTranslate('The upload is limited to 100,000 voucher codes.')
      )
      return
    }
    if (new Blob([codes]).size > MAX_FILE_BYTES) {
      setResult(null)
      setInputError(getTranslate('The voucher input is too large.'))
      return
    }
    setInputError('')
    upload({
      variables: { input: { promotionId: id, codes: normalizedCodes, commit } },
      onCompleted: (data) => {
        // Ignore responses for an input that the user changed while the request was in flight.
        if (codesRef.current !== codes) return
        setResult(data.uploadVouchers)
        if (commit) {
          setCommitted(true)
          setValidatedCodes(null)
        } else {
          setValidatedCodes(codes)
        }
      },
      onError: () => toast.error(getTranslate('Unable to upload vouchers.'))
    })
  }

  const readFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return
    updateCodes('')
    if (file.size > MAX_FILE_BYTES) {
      setInputError(getTranslate('The selected file is too large.'))
      return
    }
    setInputError('')
    const reader = new FileReader()
    reader.onload = () => updateCodes(String(reader.result || ''))
    reader.onerror = () =>
      setInputError(getTranslate('Unable to read the selected file.'))
    reader.readAsText(file)
  }

  return (
    <>
      <PageHeader title={getTranslate('Upload vouchers')} />
      <div className={classes.header}>
        <Typography variant="h3">{getTranslate('Upload vouchers')}</Typography>
        <Button component={LocalizedLink} to={`/promotions/${id}`}>
          {getTranslate('Back to promotion')}
        </Button>
      </div>
      <Paper className={classes.paper}>
        {inputError && <Alert severity="error">{inputError}</Alert>}
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography>
              {getTranslate(
                'Enter one voucher code per line. Validate before committing.'
              )}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <input
              aria-label={getTranslate('Voucher file')}
              type="file"
              accept=".txt,.csv"
              onChange={readFile}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={8}
              label={getTranslate('Voucher codes')}
              value={codes}
              onChange={(event) => updateCodes(event.target.value)}
            />
          </Grid>
        </Grid>
        <div className={classes.actions}>
          <Button
            variant="outlined"
            onClick={() => submit(false)}
            disabled={loading}>
            {getTranslate('Validate')}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => submit(true)}
            disabled={loading || committed || validatedCodes !== codes}>
            {getTranslate('Commit upload')}
          </Button>
          {loading && (
            <CircularProgress
              size={24}
              aria-label={getTranslate('Uploading vouchers')}
            />
          )}
        </div>
      </Paper>
      {result && (
        <Paper className={classes.paper}>
          <Alert severity={result.committed ? 'success' : 'info'}>
            {getTranslate(
              result.committed
                ? 'Voucher upload committed.'
                : 'Validation complete; nothing was committed.'
            )}
          </Alert>
          <Grid container spacing={2} className={classes.summary}>
            <Grid item xs={4}>
              <Typography variant="caption">
                {getTranslate('Accepted')}
              </Typography>
              <Typography>{result.accepted}</Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography variant="caption">
                {getTranslate('Duplicates')}
              </Typography>
              <Typography>{result.duplicates}</Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography variant="caption">
                {getTranslate('Invalid')}
              </Typography>
              <Typography>{result.invalid.length}</Typography>
            </Grid>
          </Grid>
          {result.invalid.length > 0 && (
            <Alert severity="warning">
              {result.invalid.slice(0, 50).join(', ')}
              {result.invalid.length > 50
                ? ` (${result.invalid.length - 50} ${getTranslate('more')})`
                : ''}
            </Alert>
          )}
        </Paper>
      )}
      {/*
        The audit sits BELOW the upload form, on the same screen, because it is the same
        subject: this promotion's voucher pool. Issue above, observe below. A separate
        route would have made two pages out of one question, and the section already has
        more navigation than it needs.
      */}
      <PromotionCodesSection promotionId={id} />
      <Button onClick={() => history.push(`/${lang}/promotions/${id}`)}>
        {getTranslate('Done')}
      </Button>
    </>
  )
}

export default VoucherUpload
